import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, AlertTriangle } from 'lucide-react';

/*
 * MetricSummary — the FIRST level of a drill-down (P140 PR B).
 *
 * Clicking a tile used to dump up to a thousand rows. It now opens here: one row
 * per stage / rep / source, with a count and a dollar total, and the row list is
 * one level deeper and 25 at a time.
 *
 * ⛔⛔ NO CONTACT NAMES AT THIS LEVEL. The card's rule, and it is enforced by the
 * server's dimension allowlist (SUMMARY_DIMENSIONS cannot name a contact column),
 * not by this component choosing what to render. A UI-side omission would be one
 * careless edit away from leaking; an allowlist that has no name in it cannot.
 *
 * ⚠ The group counts and dollars are the SAME numbers the detail reports,
 * because the server groups the identical base query the detail wraps. They are
 * not a second calculation that could drift.
 */

/*
 * ⛔ WAS A LOCAL `money()` THAT ASSUMED DOLLARS, AND WAS HANDED CENTS. Live on
 * 28 Oct it rendered one group as $97,090,900 beside a $3,265,035 tile. The unit
 * now travels with the data (`totalCents`, `unit`) and the formatter names the
 * unit it takes.
 */
import { formatCents, formatCentsRounded } from './_money';

/*
 * A NULL group key is a real group — an opportunity with no owner, a contact
 * with no referral source. It is LABELLED rather than dropped: a summary that
 * silently omitted it would not sum to the detail, and the discrepancy would be
 * invisible.
 */
const NULL_LABEL = {
  stage: '(no stage set)',
  rep: '(unassigned)',
  source: '(no source recorded)',
};

/*
 * ⚠⚠ FULL CHANNEL NAMES AS LITERALS, NOT A PREFIX (P150).
 *
 * The first cut of this took a `channelPrefix` and built
 * `${prefix}:summary`. It worked, and it broke the P123 caller ratchet — which
 * finds a channel's caller by looking for its LITERAL NAME in client code. The
 * template made four finance channels AND the two existing analytics ones read
 * as callerless, so a refactor for reuse quietly disarmed the guard that proves
 * every channel is reachable.
 *
 * Naming them in full is also plainer: a reader sees exactly which channels
 * this component talks to, instead of having to resolve a string at runtime.
 *
 * ⚠ Code-supplied by the calling page, never user input — these name IPC
 * channels, and a caller-chosen one would be a channel the server never meant
 * to expose.
 */
export const ANALYTICS_CHANNELS = Object.freeze({
  summary: 'analytics:drilldown:summary',
  resolve: 'analytics:drilldown:resolve',
  export: 'analytics:drilldown:export',
});
export default function MetricSummary({ metricKey, title, onClose, onOpenGroup, channels = ANALYTICS_CHANNELS, filters = null }) {
  /*
   * ⛔⛔ NULL UNTIL THE SERVER SAYS. This was `useState('stage')`, which is a
   * dimension only the OPPORTUNITY registry has — so the first load of any
   * finance figure sent `dimension: 'stage'`, the resolver refused it, and the
   * panel showed "That figure cannot be grouped that way" with the fallback
   * Stage / Rep / Source buttons underneath. Live on v2.1.10.
   *
   * ⚠ The component now knows NO dimension name at all. It sends none on the
   * first request; the server answers with its registry's own default and the
   * list it supports, and the client adopts both. A client that hardcodes a
   * dimension is a client that must be edited every time a registry is added —
   * and the editing is what gets forgotten.
   */
  const [dimension, setDimension] = useState(null);
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(async () => {
    setState({ loading: true, error: null, data: null });
    try {
      const api = (typeof window !== 'undefined' && window.zignagenixAPI) || null;
      if (!api || typeof api.invoke !== 'function') throw new Error('Drill-down is unavailable in this context.');
      // ⚠ The key is omitted entirely when unset, not sent as null: the
      // handler's `typeof p.dimension === "string"` check is what selects the
      // registry default, and an explicit null would fail it the same way but
      // read as a deliberate choice to anyone debugging.
      const res = await api.invoke(channels.summary,
        dimension ? { key: metricKey, dimension, filters } : { key: metricKey, filters });
      // A refusal is NOT an empty result. Surfacing the server's message keeps
      // "you may not see this" distinct from "there is nothing to see".
      if (res && res.error) throw new Error(res.message || 'That figure could not be summarised.');
      // Adopt whatever the server grouped by, so the pressed button matches
      // the data on screen rather than what the client guessed.
      if (res && res.dimension) setDimension(res.dimension);
      setState({ loading: false, error: null, data: res });
    } catch (e) {
      setState({ loading: false, error: e.message || 'That figure could not be summarised.', data: null });
    }
  }, [metricKey, dimension, channels, filters]);

  useEffect(() => { load(); }, [load]);

  const { loading, error, data } = state;
  const groups = (data && Array.isArray(data.groups)) ? data.groups : [];
  /*
   * ⚠ READ FROM THE RESPONSE, never inferred from the metric key. A renderer
   * that guessed "ceo.*_value means money" would be one metric name away from
   * the same class of bug.
   */
  const isMoney = Boolean(data && data.unit === 'cents');
  /*
   * ⛔ NO FALLBACK LIST. It used to default to the opportunity dimensions, so a
   * failed finance summary rendered Stage / Rep / Source — buttons that could
   * only produce the same refusal again. If the server did not say what this
   * figure can be grouped by, the honest answer is to offer nothing.
   */
  const dims = (data && Array.isArray(data.dimensions)) ? data.dimensions : [];

  return (
    <section data-testid="metric-summary" style={S.wrap}>
      <header style={S.head}>
        <h3 style={S.title}>{title}</h3>
        <button type="button" onClick={onClose} style={S.close} data-testid="summary-close">Close</button>
      </header>

      <div style={S.dims} role="group" aria-label="Group by">
        <span style={S.dimLabel}>Group by</span>
        {dims.map((d) => (
          <button
            key={d.key}
            type="button"
            data-testid={`summary-dim-${d.key}`}
            aria-pressed={dimension === d.key}
            onClick={() => setDimension(d.key)}
            style={dimension === d.key ? S.dimOn : S.dimOff}
          >
            {d.label}
          </button>
        ))}
      </div>

      {loading && <div style={S.muted} data-testid="summary-loading">Loading summary…</div>}

      {/*
        * ⚠ An error is rendered as an ERROR, never as an empty table. A failed
        * summary that looked like "no groups" would read as "this figure has
        * nothing behind it" — the false-zero shape again, one level up.
        */}
      {error && (
        <div style={S.error} data-testid="summary-error">
          <AlertTriangle size={15} />
          <span style={{ marginLeft: 8 }}>{error}</span>
        </div>
      )}

      {!loading && !error && groups.length === 0 && (
        <div style={S.muted} data-testid="summary-empty">No rows behind this figure.</div>
      )}

      {!loading && !error && groups.length > 0 && (
        <table style={S.table} data-testid="summary-table">
          <thead>
            <tr>
              <th style={S.th}>{(data && data.dimensionLabel) || 'Group'}</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Count</th>
              <th style={{ ...S.th, textAlign: 'right' }}>Value</th>
              <th style={S.th} aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {groups.map((g, i) => (
              <tr key={`${g.key === null ? '__null__' : g.key}-${i}`} style={S.tr}>
                <td style={S.td} data-testid={`summary-group-${g.key === null ? '__null__' : g.key}`}>
                  {/*
                    * ⚠ THE SERVER'S LABEL, not the storage key. `d90_plus`
                    * reached the CEO's screen verbatim on v2.1.12. The
                    * dimension owns its vocabulary and sends the words; this
                    * falls back to the key only when it sent none, which is the
                    * case for dimensions whose values are already human.
                    */}
                  {g.key === null
                    ? (g.label || NULL_LABEL[dimension] || '(not set)')
                    : (g.label || g.key)}
                </td>
                <td style={{ ...S.td, textAlign: 'right' }}>{g.n}</td>
                <td style={{ ...S.td, textAlign: 'right', fontWeight: 600 }}
                    data-testid={`summary-value-${g.key === null ? '__null__' : g.key}`}>
                  {isMoney ? formatCents(g.totalCents) : Number(g.totalCents).toLocaleString('en-US')}
                </td>
                <td style={{ ...S.td, textAlign: 'right' }}>
                  <button
                    type="button"
                    data-testid={`summary-open-${g.key === null ? '__null__' : g.key}`}
                    onClick={() => onOpenGroup && onOpenGroup({ dimension, key: g.key, label: g.key === null ? (NULL_LABEL[dimension] || '(not set)') : g.key })}
                    style={S.open}
                  >
                    View rows <ChevronRight size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td style={S.tf}>Total</td>
              <td style={{ ...S.tf, textAlign: 'right' }} data-testid="summary-total-count">{data.rowCount}</td>
              <td style={{ ...S.tf, textAlign: 'right' }} data-testid="summary-total-value">
                {isMoney ? formatCents(data.totalCents) : Number(data.totalCents).toLocaleString('en-US')}
              </td>
              <td style={S.tf} />
            </tr>
          </tfoot>
        </table>
      )}
    </section>
  );
}

const S = {
  wrap: { border: '1px solid #DEE2E6', borderRadius: 12, padding: 16, marginTop: 16, background: '#fff' },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  title: { margin: 0, fontSize: 15, fontWeight: 700, color: '#212529' },
  close: { background: 'none', border: '1px solid #CED4DA', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 12 },
  dims: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 },
  dimLabel: { fontSize: 12, color: '#6C757D', marginRight: 4 },
  dimOn: { border: '1px solid #212529', background: '#212529', color: '#fff', borderRadius: 8, padding: '4px 11px', fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  dimOff: { border: '1px solid #CED4DA', background: '#fff', color: '#495057', borderRadius: 8, padding: '4px 11px', fontSize: 12, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #DEE2E6', color: '#212529', fontWeight: 700, fontSize: 12 },
  tr: { borderBottom: '1px solid #F1F3F5' },
  td: { padding: '8px 12px', color: '#343A40' },
  tf: { padding: '9px 12px', borderTop: '2px solid #DEE2E6', fontWeight: 700, color: '#212529' },
  open: { display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', color: '#1864AB', cursor: 'pointer', fontSize: 12, font: 'inherit', fontWeight: 600 },
  muted: { fontSize: 13, color: '#ADB5BD', padding: '10px 2px' },
  error: { display: 'flex', alignItems: 'center', padding: 11, borderRadius: 9, background: '#FDECEA', color: '#7B241C', fontSize: 13 },
};
